import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { ConsentBanner } from "@/components/core/consent-banner";
import { Footer } from "@/components/core/footer";
import { Navbar } from "@/components/core/navbar";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { SkipLink } from "@/components/ui/skip-link";
import { useSiteContent, useSiteShared } from "@/config/use-site-content";
import { useT } from "@/i18n/locale-context";
import { createThemeStyle } from "@/lib/site-config";

interface LayoutShellProps {
  children: ReactNode;
}

export function LayoutShell({ children }: LayoutShellProps): JSX.Element {
  const content = useSiteContent();
  const shared = useSiteShared();
  const t = useT();
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const lastSegment = pathSegments.at(-1) ?? "";
  const compactFooter =
    lastSegment === "about" ||
    lastSegment === "inquiry" ||
    lastSegment === "legal";

  return (
    <div style={createThemeStyle(shared.theme)}>
      <SkipLink>{t("common.skipToContent")}</SkipLink>
      <Navbar items={content.navigation} siteName={content.site.name} />
      <main id="main" tabIndex={-1}>
        <ErrorBoundary
          copy={{
            eyebrow: t("errors.boundaryEyebrow"),
            hint: t("errors.boundaryHint"),
            retryLabel: t("actions.tryAgain"),
            title: t("errors.boundaryTitle"),
          }}
        >
          {children}
        </ErrorBoundary>
      </main>
      <Footer
        compact={compactFooter}
        groups={content.footerGroups}
        siteName={content.site.name}
        socialLinks={shared.socialLinks}
        tagline={content.site.tagline}
      />
      <ConsentBanner />
    </div>
  );
}
